import { useCallback, useState } from 'react';
import { Focus, KeyboardEventLike, Transitions } from '../navigation/typings';
import { resolve } from '../navigation/resolve';
import { SidebarColumn } from './types';
import { fromCoords } from '../navigation/utils';
import { Control } from '@yleisradio/areena-types';
import useGoBackOrGoHome from '../Routes/redirects/useGoBackOrGoHome';
import { useLocationWithState } from '../Routes/useLocationWithState';
import getRoutePath from '../Routes/getRoutePath';
import { useInputMethodContext } from '../contexts/InputMethodContext/InputMethodContext';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import { App } from '@yleisradio/areena-types/domain/app';
import { filterSideBarMenuGroupings } from './utils';
import closeAppByMode from '../utils/closeAppByMode';

const remapSidebarFocusColumn = (
  focusMenu: Focus,
  focusBack: Focus,
  isSidebarMenu: boolean
): Focus => {
  return fromCoords(
    isSidebarMenu
      ? { ...focusMenu, column: SidebarColumn.Menu }
      : { ...focusBack, column: SidebarColumn.Back }
  );
};

const focusBack: Focus = fromCoords({
  row: 0,
  column: 0,
  offset: 0,
});

const sidebarBackContainer = {
  content: [focusBack.id],
  constraint: { width: 1 },
};

const useRouteToInitialFocus = (controls: Control[]) => {
  const location = useLocationWithState();

  const getInitialFocusOffset = useCallback(() => {
    return controls.findIndex(
      control =>
        getRoutePath({
          pointer: control.destination,
          functionality: control.functionality,
        }) === location.pathname
    );
  }, [controls, location]);

  const offset = getInitialFocusOffset();

  return fromCoords({
    row: 0,
    column: 0,
    offset: offset >= 0 ? offset : 1,
  });
};

const useSidebarNavigation = (
  initialFocus: Focus,
  menuControls: Control[],
  isSidebarVisible: boolean,
  isSidebarOpen: boolean,
  isSidebarMenu: boolean,
  closeSidebar: () => void
) => {
  const [focusMenu, setFocusMenu] = useState<Focus>(initialFocus);

  const sidebarMenuContainer = {
    content: menuControls,
    constraint: { width: 1 },
  };

  const transitions: Transitions = {
    NEXT: args => {
      closeSidebar();
      return args.currentFocus;
    },
    PREV: args => args.currentFocus,
    BELOW: args => args.currentFocus,
    ABOVE: args => args.currentFocus,
  };

  const navigate = (e: KeyboardEventLike) => {
    if (isSidebarVisible && isSidebarOpen) {
      const activeContainer = isSidebarMenu
        ? sidebarMenuContainer
        : sidebarBackContainer;

      const newFocus = resolve({
        currentFocus: focusMenu,
        currentContainer: activeContainer,
        keyCode: e.keyCode,
        getContainer: () => activeContainer,
        transitions,
      });

      if (isSidebarMenu) {
        setFocusMenu(newFocus);
      }
    }
  };

  return {
    focus: isSidebarVisible
      ? remapSidebarFocusColumn(focusMenu, focusBack, isSidebarMenu)
      : null,
    navigate,
  };
};

export const useSidebar = ({
  isVisible,
  isOpen,
  isMenu,
  close,
  appData,
}: Props) => {
  const { mode } = useAppFeaturesContext();
  const { user } = useAuthContext();
  const isLoggedIn = !!user;

  const groupings = filterSideBarMenuGroupings(appData, mode, isLoggedIn);
  const { inputMethod } = useInputMethodContext();
  const isKeyboardMethod = inputMethod === 'keyboard';

  const controls = groupings.flatMap(grouping => grouping.controls);

  const initialFocus = useRouteToInitialFocus(controls);

  const { focus, navigate } = useSidebarNavigation(
    initialFocus,
    controls,
    Boolean(isVisible),
    isOpen,
    isMenu,
    close
  );

  const goBackOrGoHome = useGoBackOrGoHome();

  return {
    closeApp: () => closeAppByMode(mode),
    focus,
    goBackOrGoHome,
    groupings,
    isKeyboardMethod,
    navigate,
  };
};

type Props = {
  isOpen: boolean;
  isMenu: boolean;
  isVisible: boolean;
  close: () => void;
  appData: App | undefined;
};
